import { useEffect, useRef, useState } from "react"
import useAppDispatch from "hooks/useAppDispatch"
import { getSDIncidents } from "store/StaticData/SDIncidents.slice"
import IncidentModal from "pages/workMonitor/IncidentModal"
import useAppSelector from "hooks/useAppSelector"
import {
	getLocationIncidents,
	getLocationShiftIncidents,
	isLocationIncidentsLoading,
	locationIncidentsList,
	locationShiftIncidentsList,
	saveIncident,
} from "store/Location/locationIncidents.slice"
import { AntdButton, Tab, Tabs } from "components"
import { CloudDownloadOutlined, PlusOutlined } from "@ant-design/icons"
import { IncidentInterface } from "@type/workMonitor.types"
import moment from "moment"
import { DATE_FORMATS } from "config/constants"
import { Table } from "antd-v5"
import IncidentReportModal from "pages/locations/Incidents/IncidentReportModal"
import { generatePDF } from "pages/schedules/Location/IncidentInfo"
import useSchedulesQueryParams from "hooks/useSchedulesQueryParams"
import { useLocationScheduleDefaults } from "hooks/useScheduleDefaults"

const Incidents: React.FC = () => {
	const [openModal, setOpenModal] = useState(false)
	const dispatch = useAppDispatch()
	useLocationScheduleDefaults()
	const { selectedLocation = "" } = useSchedulesQueryParams()
	const incidentList = useAppSelector(locationIncidentsList())
	const shiftsIncidentList = useAppSelector(locationShiftIncidentsList())
	const incidentsLoading = useAppSelector(isLocationIncidentsLoading())
	const [selectedIncident, setSelectedIncident] = useState<IncidentInterface | null>(null)
	const [showReport, setShowReport] = useState(false)

	const defaultIncident: IncidentInterface = {
		_id: "",
		incident: "",
		type: "",
		officer: { _id: "", firstName: "", lastName: "" },
		site: { _id: "", name: "", shortName: "" },
		date: "",
		eventTime: "",
		isPoliceRequested: false,
		reference: "",
		actionTaken: false,
		controlInformed: false,
		details: "",
		images: [],
		geo: {
			type: "",
			coordinates: [],
			_id: "",
		},
		reportingOfficer: { _id: "", firstName: "", lastName: "" },
		signature: "",
		incidentState: undefined,
		resolvedAt: undefined,
		resolvedBy: { _id: "", firstName: "", lastName: "" },
		incidentComments: [],
	}

	const [reportData, setReportData] = useState<IncidentInterface>(defaultIncident)

	useEffect(() => {
		dispatch(getSDIncidents())
	}, [])

	useEffect(() => {
		dispatch(getLocationIncidents(selectedLocation))
		dispatch(getLocationShiftIncidents(selectedLocation))
	}, [selectedLocation])
	const htmlRef = useRef<HTMLDivElement>(null)

	return (
		<>
			<Tabs initialTab={0}>
				<Tab title="Site Incidents" hasHeader shouldOverflow={false}>
					<Table
						loading={incidentsLoading}
						size="small"
						pagination={{
							position: ["bottomRight"],
							defaultPageSize: 12,
							pageSize: 12,
						}}
						dataSource={incidentList}
						columns={[
							{
								title: "Time Of Event",
								key: "date",
								dataIndex: "date",
								render: value => <div> {moment(value).format(DATE_FORMATS.DD_MM_YYYY_HH_MM)}</div>,
							},
							{
								title: "Incident",
								key: "incident",
								dataIndex: "incident",
							},
							{
								title: "Action Taken",
								key: "actionTaken",
								dataIndex: "actionTaken",
								render: value => <div> {value ? "Y" : "N"}</div>,
							},
							{
								title: "Control Informed",
								key: "controlInformed",
								dataIndex: "controlInformed",
								render: value => <div> {value ? "Y" : "N"}</div>,
							},
							{
								title: "Police Requested",
								key: "isPoliceRequested",
								dataIndex: "isPoliceRequested",
								render: value => <div> {value ? "Y" : "N"}</div>,
							},
							{
								title: "Action",
								key: "",
								dataIndex: "",
								render: (_, row) => (
									<AntdButton
										onClick={() => {
											setSelectedIncident(selectedRow =>
												selectedRow?._id === row._id ? null : row,
											)
											setOpenModal(true)
										}}
									>
										{selectedIncident === row ? "Editing." : "Update"}
									</AntdButton>
								),
							},
							{
								title: "Report",
								key: "",
								render: (_, row) => (
									<AntdButton
										className="flex items-center justify-center"
										icon={<CloudDownloadOutlined />}
										onClick={() => generatePDF({ ...row, siteName: row.site?.name } as any)}
									>
										Download
									</AntdButton>
								),
							},
							// {
							// 	title: "Report",
							// 	key: "",
							// 	render: (_, row) => (
							// 		<AntdButton
							// 			className="flex items-center justify-center"
							// 			icon={<CloudDownloadOutlined />}
							// 			onClick={() => {
							// 				setReportData(row)
							// 				setShowReport(true)

							// 				// Trigger after render
							// 				setTimeout(() => {
							// 					document.getElementById("trigger-download")?.click()
							// 				}, 600)
							// 			}}
							// 		>
							// 			Download
							// 		</AntdButton>
							// 	),
							// },
							{
								title: "View Report",
								key: "",
								render: (_, row) => (
									<AntdButton
										className="flex items-center justify-center"
										onClick={() => {
											setReportData(row)
											setShowReport(true)
										}}
									>
										View
									</AntdButton>
								),
							},
						]}
					/>
					<div className="flex justify-end pt-4">
						<AntdButton icon={<PlusOutlined />} onClick={() => setOpenModal(val => !val)}>
							Add New
						</AntdButton>
					</div>
				</Tab>
				<Tab title="Shift Incidents" hasHeader shouldOverflow={false}>
					<Table
						size="small"
						pagination={{
							position: ["bottomRight"],
							defaultPageSize: 12,
							pageSize: 12,
						}}
						dataSource={shiftsIncidentList}
						columns={[
							{
								title: "Time Of Event",
								key: "date",
								dataIndex: "date",
								render: value => <div> {moment(value).format(DATE_FORMATS.DD_MM_YYYY_HH_MM)}</div>,
							},
							{
								title: "Incident",
								key: "incident",
								dataIndex: "incident",
							},
							{
								title: "Action Taken",
								key: "actionTaken",
								dataIndex: "actionTaken",
								render: value => <div> {value ? "Y" : "N"}</div>,
							},
							{
								title: "Control Informed",
								key: "controlInformed",
								dataIndex: "controlInformed",
								render: value => <div> {value ? "Y" : "N"}</div>,
							},
							{
								title: "Police Requested",
								key: "isPoliceRequested",
								dataIndex: "isPoliceRequested",
								render: value => <div> {value ? "Y" : "N"}</div>,
							},
							{
								title: "Report",
								key: "",
								render: (_, row) => (
									<AntdButton
										className="flex items-center justify-center"
										icon={<CloudDownloadOutlined />}
										onClick={() => generatePDF({ ...row, siteName: row.site?.name } as any)}
									>
										Download
									</AntdButton>
								),
							},
							// {
							// 	title: "Report",
							// 	key: "",
							// 	render: (_, row) => (
							// 		<AntdButton
							// 			className="flex items-center justify-center"
							// 			icon={<CloudDownloadOutlined />}
							// 			onClick={() => {
							// 				setReportData(row)
							// 				setShowReport(true)

							// 				// Trigger after render
							// 				setTimeout(() => {
							// 					document.getElementById("trigger-download")?.click()
							// 				}, 600)
							// 			}}
							// 		>
							// 			Download
							// 		</AntdButton>
							// 	),
							// },
							{
								title: "View Report",
								key: "",
								render: (_, row) => (
									<AntdButton
										className="flex items-center justify-center"
										onClick={() => {
											setReportData(row) // ✅ pass full row into props
											setShowReport(true)
										}}
									>
										View
									</AntdButton>
								),
							},
						]}
					/>
				</Tab>
			</Tabs>

			<IncidentReportModal
				open={showReport}
				onClose={() => setShowReport(false)}
				reportData={reportData}
				componentRef={htmlRef}
			/>

			<IncidentModal
				isForLocation
				open={openModal}
				modalHandler={(open: boolean) => {
					setOpenModal(open)
					setSelectedIncident(null)
				}}
				selectedRow={null}
				defaultFields={selectedIncident ?? undefined}
				submitHandler={data => {
					dispatch(saveIncident({ ...data, _id: data?._id, site: selectedLocation }))
				}}
			/>
		</>
	)
}
export default Incidents
